import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Combatant, CombatantPublicView, ConditionType, MapState } from '@sd/shared';
import { CONDITION_BY_TYPE } from './conditions.js';
import type { FxEvent } from '../../hooks/useFxQueue.js';
import { Icon } from '../adventures/Icon.js';
import { iconTypeFor } from '../adventures/icons.js';

type AnyToken = (Combatant | CombatantPublicView) & { stats?: Combatant['stats'] };

interface Props {
  map: MapState;
  combatants: AnyToken[];
  canMove: (combatantId: string) => boolean;
  onMove: (id: string, x: number, y: number) => void;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  fxs?: FxEvent[];
  /** Поточна ціль (для target-ring + attack-arcs) */
  targetId?: string | null;
  /** Клік на token, який не «свій» — викликає це. Якщо нема — fallback на onSelect. */
  onSetTarget?: (id: string | null) => void;
  /** Якщо true — клік на token = ціль (а не selectedId). Ігнорується, якщо передано renderQuickMenu. */
  clickSetsTarget?: boolean;
  /** Render-prop: коли передано, короткий клік на токен відкриває спливаюче меню під ним. */
  renderQuickMenu?: (token: AnyToken, close: () => void) => JSX.Element | null;
}

const CELL = 52;
const DIST_BANDS = [
  { name: 'Melee',     cells: 1, color: 'rgba(157,79,74,0.25)' },
  { name: 'Very Close', cells: 3, color: 'rgba(204,167,102,0.20)' },
  { name: 'Close',     cells: 6, color: 'rgba(121,169,142,0.15)' },
  { name: 'Far',       cells: 12, color: 'rgba(111,140,160,0.10)' },
];

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 3;
const DRAG_THRESHOLD_PX = 4;

interface View { x: number; y: number; zoom: number }

export function TacticalMap({ map, combatants, canMove, onMove, selectedId, onSelect, fxs = [], targetId, onSetTarget, clickSetsTarget, renderQuickMenu }: Props): JSX.Element {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragXY, setDragXY] = useState<{ x: number; y: number } | null>(null);
  const dragMovedRef = useRef(false);
  const downPosRef = useRef<{ x: number; y: number } | null>(null);
  /** Quick-menu state (id токена, для якого зараз відкрите меню). */
  const [quickMenuId, setQuickMenuId] = useState<string | null>(null);

  /** Камера: екранне зміщення canvas + scale. */
  const [view, setView] = useState<View>({ x: 32, y: 32, zoom: 1 });
  const panStateRef = useRef<{ start: { x: number; y: number }; view: View; moved: boolean } | null>(null);

  /** Короткий клік на token. Якщо є renderQuickMenu — toggle меню; інакше — fallback на стару поведінку. */
  const handleClickAction = useCallback((id: string) => {
    if (renderQuickMenu) {
      setQuickMenuId((prev) => (prev === id ? null : id));
      return;
    }
    if (!onSetTarget || !clickSetsTarget) {
      onSelect?.(id);
      return;
    }
    onSetTarget(targetId === id ? null : id);
  }, [renderQuickMenu, clickSetsTarget, onSelect, onSetTarget, targetId]);

  /** Авто-fit при першому маунті (щоб мапа влізла у viewport). */
  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const w = vp.clientWidth;
    const h = vp.clientHeight;
    const mapW = map.cols * CELL;
    const mapH = map.rows * CELL;
    if (w === 0 || h === 0 || mapW === 0 || mapH === 0) return;
    const zoom = Math.min(1, Math.min((w - 32) / mapW, (h - 32) / mapH));
    setView({
      x: Math.max(16, (w - mapW * zoom) / 2),
      y: Math.max(16, (h - mapH * zoom) / 2),
      zoom,
    });
    // лише при зміні розмірів мапи; кейс ресайзу viewport обробляється окремим observer-ом
  }, [map.cols, map.rows]);

  /** Pan: drag по background мапи. Стартуємо лише якщо натиск саме на viewport (а не на токен). */
  const onViewportPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    // Натиск на токен пропускає на onPointerDown токена; сюди прилітає лише background.
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    panStateRef.current = {
      start: { x: e.clientX, y: e.clientY },
      view: { ...view },
      moved: false,
    };
  }, [view]);

  const onViewportPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const pan = panStateRef.current;
    if (!pan) return;
    const dx = e.clientX - pan.start.x;
    const dy = e.clientY - pan.start.y;
    if (!pan.moved && Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) pan.moved = true;
    setView({ x: pan.view.x + dx, y: pan.view.y + dy, zoom: pan.view.zoom });
  }, []);

  const onViewportPointerUp = useCallback(() => {
    const pan = panStateRef.current;
    if (!pan) return;
    if (!pan.moved) {
      // клік на порожнечу — закрити quick-menu і скинути виділення
      setQuickMenuId(null);
      onSelect?.(null);
    }
    panStateRef.current = null;
  }, [onSelect]);

  /** Zoom колесом — фіксуємо точку під курсором (її world-coord залишається той самий). */
  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!viewportRef.current) return;
    e.preventDefault();
    const rect = viewportRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    setView((v) => {
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const nz = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.zoom * factor));
      if (nz === v.zoom) return v;
      const wx = (cx - v.x) / v.zoom;
      const wy = (cy - v.y) / v.zoom;
      return { x: cx - wx * nz, y: cy - wy * nz, zoom: nz };
    });
  }, []);

  /** Token pointer-down: drag або клік-ціль. Координати у клітинках = (screen - view) / (CELL*zoom). */
  const onTokenPointerDown = useCallback((e: React.PointerEvent, id: string): void => {
    if (e.button === 2) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    if (e.shiftKey && onSetTarget) {
      e.preventDefault();
      onSetTarget(targetId === id ? null : id);
      return;
    }
    if (!canMove(id)) {
      handleClickAction(id);
      return;
    }
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragId(id);
    dragMovedRef.current = false;
    downPosRef.current = { x: e.clientX, y: e.clientY };
    // якщо це переростає у drag — меню має зникнути; ставимо null лише коли реально зрушив (нижче в onTokenPointerMove)
  }, [canMove, onSetTarget, targetId, handleClickAction]);

  const onTokenPointerMove = useCallback((e: React.PointerEvent): void => {
    if (!dragId || !viewportRef.current) return;
    if (!dragMovedRef.current && downPosRef.current) {
      const dx = e.clientX - downPosRef.current.x;
      const dy = e.clientY - downPosRef.current.y;
      if (Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) {
        dragMovedRef.current = true;
        // Реальний drag → закрити quick-menu, якщо було відкрите
        setQuickMenuId(null);
      }
    }
    const rect = viewportRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const x = (sx - view.x) / (CELL * view.zoom);
    const y = (sy - view.y) / (CELL * view.zoom);
    setDragXY({ x: Math.max(0, Math.min(map.cols, x)), y: Math.max(0, Math.min(map.rows, y)) });
  }, [dragId, map.cols, map.rows, view.x, view.y, view.zoom]);

  const onTokenPointerUp = useCallback((): void => {
    if (dragId) {
      if (dragMovedRef.current && dragXY) {
        const snappedX = Math.round(dragXY.x - 0.5) + 0.5;
        const snappedY = Math.round(dragXY.y - 0.5) + 0.5;
        onMove(dragId, snappedX, snappedY);
      } else {
        handleClickAction(dragId);
      }
    }
    setDragId(null);
    setDragXY(null);
    dragMovedRef.current = false;
    downPosRef.current = null;
  }, [dragId, dragXY, onMove, handleClickAction]);

  /** Права кнопка — вибрати юніт для detail-картки (GM). */
  const onTokenContextMenu = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(id);
  }, [onSelect]);

  useEffect(() => {
    return () => { setDragId(null); setDragXY(null); panStateRef.current = null; };
  }, []);

  /** Якщо токен з відкритим меню зник з combatants — авто-закрити. */
  useEffect(() => {
    if (quickMenuId && !combatants.some((c) => c.id === quickMenuId)) {
      setQuickMenuId(null);
    }
  }, [combatants, quickMenuId]);

  /** Esc закриває меню (a11y). */
  useEffect(() => {
    if (!quickMenuId) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setQuickMenuId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [quickMenuId]);

  const selectedToken = selectedId ? combatants.find((c) => c.id === selectedId) : null;

  const resetView = (): void => {
    const vp = viewportRef.current; if (!vp) return;
    const w = vp.clientWidth, h = vp.clientHeight;
    const mapW = map.cols * CELL, mapH = map.rows * CELL;
    const zoom = Math.min(1, Math.min((w - 32) / mapW, (h - 32) / mapH));
    setView({
      x: Math.max(16, (w - mapW * zoom) / 2),
      y: Math.max(16, (h - mapH * zoom) / 2),
      zoom,
    });
  };
  const zoomBy = (factor: number): void => {
    const vp = viewportRef.current; if (!vp) return;
    const rect = vp.getBoundingClientRect();
    const cx = rect.width / 2, cy = rect.height / 2;
    setView((v) => {
      const nz = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.zoom * factor));
      if (nz === v.zoom) return v;
      const wx = (cx - v.x) / v.zoom;
      const wy = (cy - v.y) / v.zoom;
      return { x: cx - wx * nz, y: cy - wy * nz, zoom: nz };
    });
  };

  return (
    <div
      ref={viewportRef}
      className="tm-viewport"
      onPointerDown={onViewportPointerDown}
      onPointerMove={(e) => { onViewportPointerMove(e); onTokenPointerMove(e); }}
      onPointerUp={(e) => { onViewportPointerUp(); onTokenPointerUp(); void e; }}
      onPointerCancel={(e) => { panStateRef.current = null; onTokenPointerUp(); void e; }}
      onWheel={onWheel}
      onContextMenu={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }}
    >
      <div
        className="tm-canvas tactical-map"
        style={{
          width: `${map.cols * CELL}px`,
          height: `${map.rows * CELL}px`,
          ['--cell' as string]: `${CELL}px`,
          transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.zoom})`,
          transformOrigin: '0 0',
        }}
      >
        {selectedToken && <DistanceBands token={selectedToken} />}

        {fxs.filter((f) => f.kind === 'arc' && f.fromId).map((f) => {
          const from = combatants.find((c) => c.id === f.fromId);
          const to = combatants.find((c) => c.id === f.targetId);
          if (!from || !to) return null;
          return <FxArc key={f.id} from={from} to={to} projKind={f.projKind ?? 'melee'} />;
        })}

        {combatants.filter((c) => !('hidden' in c) || !c.hidden).map((c) => {
          const isDragging = dragId === c.id;
          const x = isDragging && dragXY ? dragXY.x : c.tokenX;
          const y = isDragging && dragXY ? dragXY.y : c.tokenY;
          const kind = c.kind;
          const initial = (c.name?.[0] ?? '?').toUpperCase();
          const tokenFxs = fxs.filter((f) => f.targetId === c.id);
          const isHit = tokenFxs.some((f) => f.kind === 'hit');
          const isCrit = tokenFxs.some((f) => f.kind === 'crit');
          const isHealed = tokenFxs.some((f) => f.kind === 'heal');
          const isCasting = tokenFxs.some((f) => f.kind === 'cast');
          const isDying = tokenFxs.some((f) => f.kind === 'death');
          const animClass = isCrit ? 'fx-anim-crit' : isHit ? 'fx-anim-hit' : isDying ? 'fx-anim-death' : '';
          const isTarget = targetId === c.id;
          return (
            <div
              key={c.id}
              className={`token ${kind} ${isDragging ? 'dragging' : ''} ${selectedId === c.id ? 'selected' : ''} ${isTarget ? 'target-marker' : ''} ${animClass}`}
              style={{
                left: `${x * CELL}px`,
                top: `${y * CELL}px`,
                background: c.tokenColor ? `radial-gradient(circle at 35% 30%, ${lighten(c.tokenColor, 1.2)}, ${darken(c.tokenColor, 0.5)} 80%)` : undefined,
              }}
              onPointerDown={(e) => onTokenPointerDown(e, c.id)}
              onContextMenu={(e) => onTokenContextMenu(e, c.id)}
              title={`${c.name} · клік: ціль · ПКМ: вибрати`}
            >
              {c.illustration ? (
                <img className="token-illust" src={c.illustration} alt="" draggable={false} />
              ) : c.icon ? (
                <Icon type={iconTypeFor(c.icon)} size={26} stroke={1.8} color="rgba(20,22,15,0.9)" />
              ) : (
                initial
              )}

              {isCasting && <div className="fx-cast-halo" />}
              {(isHit || isCrit) && <div className={`fx-flash ${isCrit ? 'crit' : 'hit'}`} />}
              {isHealed && (
                <>
                  <div className="fx-heal-glow" />
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="fx-heal-spark"
                      style={{
                        ['--a' as string]: `${i * 60}deg`,
                        animation: `fx-heal-spark 1.1s ${i * 0.08}s ease-out forwards`,
                        transform: `translate(-50%, -50%) rotate(${i * 60}deg) translateY(-22px)`,
                      }}
                    />
                  ))}
                </>
              )}

              {tokenFxs
                .filter((f) => f.value !== undefined && (f.kind === 'hit' || f.kind === 'crit' || f.kind === 'heal'))
                .map((f) => (
                  <div
                    key={f.id}
                    className={`fx-float-num ${f.kind === 'crit' ? 'crit' : ''} ${f.kind === 'heal' ? 'heal' : ''}`}
                  >
                    {f.kind === 'heal' ? '+' : '−'}{f.value}
                    {f.kind === 'crit' && <span style={{ fontSize: 14, marginLeft: 4, letterSpacing: 1 }}>КРИТ</span>}
                  </div>
                ))}

              <div className="token-conds">
                {c.conditions.slice(0, 4).map((cd) => {
                  const def = CONDITION_BY_TYPE.get(cd as ConditionType);
                  return <div key={cd} className="token-cond-dot" style={{ background: def?.color ?? 'var(--text-dim)' }} title={def?.label ?? cd} />;
                })}
              </div>
              <div className="token-label">{c.name}</div>
            </div>
          );
        })}
      </div>

      {/* Quick action menu під токеном (опційно, через render-prop) */}
      {renderQuickMenu && quickMenuId && (() => {
        const t = combatants.find((c) => c.id === quickMenuId);
        if (!t) return null;
        const tokenRadius = 22; // half of .token height (44px)
        const sx = view.x + t.tokenX * CELL * view.zoom;
        const sy = view.y + (t.tokenY * CELL + tokenRadius) * view.zoom + 12;
        const menu = renderQuickMenu(t, () => setQuickMenuId(null));
        if (!menu) return null;
        return (
          <div
            className="tm-quickmenu"
            style={{ left: sx, top: sy }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {menu}
          </div>
        );
      })()}

      {/* Floating zoom controls */}
      <div className="tm-controls" onPointerDown={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => zoomBy(1.2)} title="Збільшити">+</button>
        <button type="button" onClick={resetView} title="Вмістити мапу">◫</button>
        <button type="button" onClick={() => zoomBy(1 / 1.2)} title="Зменшити">−</button>
        <div className="tm-zoom-ind">{Math.round(view.zoom * 100)}%</div>
      </div>
    </div>
  );
}

function FxArc({ from, to, projKind }: { from: AnyToken; to: AnyToken; projKind: 'arrow' | 'spell' | 'melee' | 'heal-arc' }): JSX.Element {
  const x1 = from.tokenX * CELL;
  const y1 = from.tokenY * CELL;
  const x2 = to.tokenX * CELL;
  const y2 = to.tokenY * CELL;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  return (
    <>
      <div
        className={`fx-arc ${projKind}`}
        style={{
          left: x1,
          top: y1,
          width: len,
          transform: `rotate(${angle}deg)`,
        }}
      />
      <div
        className={`fx-puff ${projKind}`}
        style={{ left: x2, top: y2 }}
      />
    </>
  );
}

function DistanceBands({ token }: { token: AnyToken }): JSX.Element {
  const cx = token.tokenX * CELL;
  const cy = token.tokenY * CELL;
  return (
    <div className="distance-bands" style={{ left: cx, top: cy }}>
      {DIST_BANDS.slice().reverse().map((b) => {
        const d = b.cells * 2 * CELL;
        return (
          <div
            key={b.name}
            className="band"
            style={{
              width: d, height: d,
              background: `radial-gradient(circle, transparent 65%, ${b.color} 100%)`,
            }}
          />
        );
      })}
    </div>
  );
}

// ── Color utils ──────────────────────────────────────────────────
function lighten(hex: string, amount: number): string {
  return shiftColor(hex, amount);
}
function darken(hex: string, amount: number): string {
  return shiftColor(hex, amount);
}
function shiftColor(hex: string, factor: number): string {
  const m = /^#?([a-f\d]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  const r = Math.min(255, Math.max(0, Math.round(((n >> 16) & 0xff) * factor)));
  const g = Math.min(255, Math.max(0, Math.round(((n >> 8) & 0xff) * factor)));
  const b = Math.min(255, Math.max(0, Math.round((n & 0xff) * factor)));
  return `rgb(${r}, ${g}, ${b})`;
}
